import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const CREATED_AT_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const BUSINESS_TIMEZONE = 'Asia/Shanghai';

export function parseCreatedAt(value: string | number | null | undefined): number | null {
    if (null == value || '' === value) return null;
    // 兼容数值时间戳（毫秒或秒）与字符串日期
    if (typeof value === 'number') {
        return Number.isFinite(value) ? normalizeTimestampMs(value) : null;
    }
    if (typeof value !== 'string') return null;
    // LocalDateTime 不携带时区：后端约定其业务语义为 Asia/Shanghai。
    // 同时兼容 Jackson ISO(T) 与数据库风格(空格)，避免交给浏览器按本地时区猜测。
    const normalized = value.trim().replace('T', ' ').replace(/\.\d+$/, '');
    const parsed = dayjs.tz(normalized, CREATED_AT_FORMAT, BUSINESS_TIMEZONE);
    const strictlyMatched = parsed.isValid() && parsed.format(CREATED_AT_FORMAT) === normalized;
    return strictlyMatched ? parsed.valueOf() : null;
}

export function toUtcSeconds(ts: number): number {
    if (ts > 10000000000) return Math.floor(ts / 1000);
    return Math.floor(ts);
}

export function normalizeTimestampMs(ts: number): number {
    if (!Number.isFinite(ts)) return Number.NaN;
    return ts > 10_000_000_000 ? ts : ts * 1000;
}

export function formatSignalTime(ts: number): string {
    if (!Number.isFinite(ts)) return '--';
    return dayjs(normalizeTimestampMs(ts)).tz(BUSINESS_TIMEZONE).format('MM-DD HH:mm');
}

export function formatRelativeTime(ts: number): string {
    return dayjs(normalizeTimestampMs(ts)).fromNow();
}

export function formatTimeTooltip(ts: number): string {
    if (!Number.isFinite(ts)) return '--';
    return `${dayjs(normalizeTimestampMs(ts)).tz(BUSINESS_TIMEZONE).format(CREATED_AT_FORMAT)} Asia/Shanghai`;
}

export function formatPrice(v: number | null | undefined): string {
    if (null == v || !Number.isFinite(v)) return '--';
    if (v >= 1000) return v.toLocaleString('en-US', {maximumFractionDigits: 2});
    if (v >= 1) return v.toFixed(2);
    return v.toFixed(4);
}

/**
 * 将K线周期字符串转换为毫秒数
 * 支持格式: 1m, 5m, 15m, 30m, 1H, 2H, 4H, 1D, 1W
 */
export function intervalToMillis(interval: string): number {
    const match = /^(\d+)([mMhHdDwW])$/.exec(interval.trim());
    if (!match) return 60 * 60 * 1000;
    const amount = Number(match[1]);
    const unit = match[2].toLowerCase();
    if ('m' === unit) return amount * 60 * 1000;
    if ('h' === unit) return amount * 60 * 60 * 1000;
    if ('d' === unit) return amount * 24 * 60 * 60 * 1000;
    return amount * 7 * 24 * 60 * 60 * 1000;
}

/**
 * 计算信号形成到识别之间的K线间隔数
 * @param formationTime - 信号形成时间（K线结束时间戳，秒或毫秒）
 * @param recognizedAt  - 识别时间戳（毫秒，parseCreatedAt 返回值，可为 null）
 * @param interval      - K线周期，如 '1H', '4H', '1D', '15m'
 * @returns K线间隔数；-1 表示无法计算
 */
export function calcKlineGap(
    formationTime: number,
    recognizedAt: number | null,
    interval: string = '1H',
): number {
    if (null == recognizedAt) return -1;
    if (!Number.isFinite(formationTime) || formationTime <= 0) return -1;
    if (!Number.isFinite(recognizedAt)) return -1;

    const intervalMs = intervalToMillis(interval);
    const diffMs = recognizedAt - normalizeTimestampMs(formationTime);
    return Math.max(0, Math.round(diffMs / intervalMs));
}

/**
 * 根据K线间隔数返回边框动画 class
 * - gap <= 1 → 'signal-dot-active'    运动炫光点
 * - gap <= 3 → 'signal-breathe-active' 呼吸闪烁边框
 * - gap > 3  → ''                      无效果
 * - gap < 0  → ''                      无法计算
 */
export function getSignalBorderClass(gap: number): string {
    if (gap < 0 || gap > 3) return '';
    if (gap <= 1) return 'signal-dot-active';
    return 'signal-breathe-active';
}
